/**
 * Analytics Service — DocuMind Summit 2026
 *
 * Sends anonymous engagement events to the backend analytics API.
 * All events are fire-and-forget — failures are silently swallowed
 * so tracking never blocks the user experience.
 *
 * Event types:
 *   flow_step      — main demo journey: home → process → review → report
 *   module_visit   — optional deep-dive modules: guardrails, architecture, etc.
 *   feedback       — thumbs up / down CSAT rating
 *   time_on_section — duration on a page section (heatmap data)
 *
 * Session ID:
 *   A UUID is generated once per browser session and stored in sessionStorage.
 *   It carries no user identity — it's used only to count unique journeys.
 */

import axios from 'axios';

const _API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ---------------------------------------------------------------------------
// Session ID — one per browser tab session, no PII
// ---------------------------------------------------------------------------

function getSessionId() {
  const key = 'dm_analytics_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

// ---------------------------------------------------------------------------
// Fire-and-forget POST helper
// ---------------------------------------------------------------------------

async function post(path, body) {
  try {
    console.log(`📊 [Analytics] POST ${path}`, body);
    await axios.post(`${_API_BASE}/api/v2/analytics/${path}`, body);
  } catch {
    // Intentionally silent — analytics must never break the demo
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const analyticsService = {
  /**
   * Call when the user lands on each main flow page.
   * step: 'home' | 'process' | 'review' | 'report'
   */
  trackFlowStep(step) {
    post('flow-step', { session_id: getSessionId(), step });
  },

  /**
   * Call when the user opens an optional deep-dive module.
   * module: 'guardrails' | 'strands_agents' | 'architecture' |
   *         'nova_models' | 'deployment' | 'code'
   */
  trackModuleVisit(module) {
    post('module-visit', { session_id: getSessionId(), module });
  },

  /**
   * Call when the user submits thumbs up/down feedback.
   * rating: 'up' | 'down'
   * context: optional label, e.g. 'research_results'
   */
  trackFeedback(rating, context = 'research_results') {
    post('feedback', { session_id: getSessionId(), rating, context });
  },

  /**
   * Call when the user leaves a section (on unmount or tab change).
   * page: page name, e.g. 'review'
   * section: section within the page, e.g. 'citations', 'insights', 'guardrails_tab'
   * durationMs: milliseconds spent on the section
   */
  trackTimeOnSection(page, section, durationMs) {
    if (durationMs < 500) return; // ignore accidental flickers
    post('time-on-section', {
      session_id: getSessionId(),
      page,
      section,
      duration_ms: durationMs,
    });
  },
};

export default analyticsService;
